import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import './css/main.css';
import apiService from './services/apiService';
import ItemForm from './components/ItemForm';
import Modal from './components/Modal';
import InfoPage from './components/InfoPage';
import LanguagesPage from './components/LanguagesPage';
import LoginPage from './components/LoginPage';
import { useLanguage } from './context/LanguageContext';
import { useAuth } from './context/AuthContext';

/**
 * Auth gate – shows loading screen, login page, or the actual app.
 * Cleanly separates the auth check from app hooks (Rules of Hooks).
 */
function App() {
  const { isAuthenticated, isChecking } = useAuth();

  if (isChecking) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif', color: '#666' }}>
        Laden …
      </div>
    );
  }
  if (!isAuthenticated) {
    return <LoginPage />;
  }
  return <AppMain />;
}

/**
 * Main app component (only rendered when logged in)
 * Manages the global state and coordinates all CRUD operations
 */
function AppMain() {
  const { t, language, changeLanguage } = useLanguage();
  const { username, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState({
    name_gr: '',
    name_en: '',
    energy: '',
    carbs: '',
    fat: '',
    proteins: '',
    grams: '',
    quantity: '',
    unit: '',
    created_at: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'id',
    direction: 'asc'
  });
  const [editingItem, setEditingItem] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [dbTable, setDbTable] = useState('eu');

  /**
   * Fetch records on load and on table switch
   */
  useEffect(() => {
    loadItems(dbTable);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbTable]);

  /**
   * Run search when query or items change
   */
  useEffect(() => {
    filterItems();
  }, [items, columnFilters, sortConfig]);

  /**
   * Fetch all records from the API
   */
  const loadItems = async (table) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAll(table || dbTable);
      setItems(Array.isArray(data) ? data : []);
      setFilteredItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Run full-text search
   */
  const filterItems = () => {
    let filtered = [...items];

    // Global search (across all fields)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        const nameEn = (item.name_en || '').toLowerCase();
        const nameGr = (item.name_gr || '').toLowerCase();
        return nameEn.includes(query) || nameGr.includes(query);
      });
    }

    // Column-specific filters
    if (columnFilters.name_gr) {
      const query = columnFilters.name_gr.toLowerCase();
      filtered = filtered.filter(item => 
        (item.name_gr || '').toLowerCase().includes(query)
      );
    }
    if (columnFilters.name_en) {
      const query = columnFilters.name_en.toLowerCase();
      filtered = filtered.filter(item => 
        (item.name_en || '').toLowerCase().includes(query)
      );
    }
    if (columnFilters.energy) {
      const query = columnFilters.energy.toLowerCase();
      filtered = filtered.filter(item => 
        String(item.energy || 0).includes(query)
      );
    }
    if (columnFilters.carbs) {
      const query = columnFilters.carbs.toLowerCase();
      filtered = filtered.filter(item => 
        String(item.carbs || 0).includes(query)
      );
    }
    if (columnFilters.fat) {
      const query = columnFilters.fat.toLowerCase();
      filtered = filtered.filter(item => 
        String(item.fat || 0).includes(query)
      );
    }
    if (columnFilters.proteins) {
      const query = columnFilters.proteins.toLowerCase();
      filtered = filtered.filter(item => 
        String(item.proteins || 0).includes(query)
      );
    }
    if (columnFilters.grams) {
      const query = columnFilters.grams.toLowerCase();
      filtered = filtered.filter(item => 
        String(item.grams || 0).includes(query)
      );
    }
    if (columnFilters.quantity) {
      const query = columnFilters.quantity.toLowerCase();
      filtered = filtered.filter(item => {
        const quantityStr = `${item.quantity || 0} ${item.unit || ''}`.trim();
        return quantityStr.toLowerCase().includes(query);
      });
    }
    if (columnFilters.unit) {
      const query = columnFilters.unit.toLowerCase();
      filtered = filtered.filter(item =>
        (item.unit || '').toLowerCase().includes(query)
      );
    }
    if (columnFilters.created_at) {
      const query = columnFilters.created_at.toLowerCase();
      filtered = filtered.filter(item => {
        if (!item.created_at) return false;
        const dateStr = new Date(item.created_at).toLocaleString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        return dateStr.toLowerCase().includes(query);
      });
    }

    // Sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Special handling for quantity (with unit)
        if (sortConfig.key === 'quantity') {
          aVal = a.quantity || 0;
          bVal = b.quantity || 0;
        }

        // Numeric values
        if (typeof aVal === 'number' || !isNaN(aVal)) {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }

        // String values
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
        
        if (sortConfig.direction === 'asc') {
          return aVal.localeCompare(bVal);
        } else {
          return bVal.localeCompare(aVal);
        }
      });
    }

    setFilteredItems(filtered);
  };

  /**
   * Reset search
   */
  const handleResetSearch = () => {
    setSearchQuery('');
    setColumnFilters({
      name_gr: '',
      name_en: '',
      energy: '',
      carbs: '',
      fat: '',
      proteins: '',
      grams: '',
      quantity: '',
      unit: '',
      created_at: ''
    });
    setSortConfig({ key: null, direction: 'asc' });
  };

  /**
   * Change column filter
   */
  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  /**
   * Change sort order
   */
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        // Same column: reverse direction or reset
        if (prev.direction === 'asc') {
          return { key, direction: 'desc' };
        } else {
          return { key: null, direction: 'asc' };
        }
      } else {
          // New column: sort ascending
        return { key, direction: 'asc' };
      }
    });
  };

  /**
   * Create new record
   */
  const handleCreate = async (formData) => {
    try {
      setError(null);
      const newItem = await apiService.create(formData, dbTable);
      setItems([newItem, ...items]);
      setIsCreateModalOpen(false);
      showSuccess(t('messages.itemCreated'));
    } catch (err) {
      setError(err.message || t('messages.error'));
    }
  };

  /**
   * Update record
   */
  const handleUpdate = async (formData) => {
    if (!editingItem) return;

    try {
      setError(null);
      const updatedItem = await apiService.update(editingItem.id, formData, dbTable);
      setItems(
        items.map((item) => (item.id === editingItem.id ? updatedItem : item))
      );
      setEditingItem(null);
      showSuccess(t('messages.itemUpdated'));
    } catch (err) {
      setError(err.message || t('messages.error'));
    }
  };

  /**
   * Delete record
   */
  const handleDelete = async (id) => {
    try {
      setError(null);
      await apiService.delete(id, dbTable);
      setItems(items.filter((item) => item.id !== id));
      showSuccess(t('messages.itemDeleted'));
    } catch (err) {
      setError(err.message || t('messages.error'));
    }
  };

  /**
   * Start edit mode
   */
  const handleEdit = (item) => {
    setEditingItem(item);
    setError(null);
  };

  /**
   * Cancel edit mode
   */
  const handleCancelEdit = () => {
    setEditingItem(null);
    setError(null);
  };

  /**
   * Open create modal
   */
  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
    setError(null);
  };

  /**
   * Close create modal
   */
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setError(null);
  };

  /**
   * Show success message (disappears after 3 seconds)
   */
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-left">
          <h1>{t('app.title')}</h1>
        </div>
        <div className="header-right">
          <div className="language-selector">
            <label htmlFor="language-select">{t('menu.selectLanguage')}:</label>
            <select 
              id="language-select"
              value={language} 
              onChange={(e) => changeLanguage(e.target.value)}
              className="language-select"
            >
              <option value="gr">{t('languages.gr')}</option>
              <option value="en">{t('languages.en')}</option>
              <option value="de">{t('languages.de')}</option>
            </select>
          </div>
          <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1rem' }}>
            <span style={{ fontSize: '0.85rem', color: '#ffffff' }}>👤 {username}</span>
            <button
              className="btn btn-secondary btn-small"
              onClick={() => logout()}
              title={t('buttons.logout')}
            >
              {t('buttons.logout')}
            </button>
          </div>
          <button className="burger-menu" onClick={() => {}} style={{ display: 'none' }}></button>
        </div>
      </header>

      <nav className="main-nav">
        <NavLink
          to="/"
          end
          className={({ isActive }) => `nav-tab${isActive ? ' nav-tab--active' : ''}`}
        >
          {t('nav.foods')}
        </NavLink>
        <NavLink
          to="/info"
          className={({ isActive }) => `nav-tab${isActive ? ' nav-tab--active' : ''}`}
        >
          {t('nav.info')}
        </NavLink>
        <NavLink
          to="/languages"
          className={({ isActive }) => `nav-tab${isActive ? ' nav-tab--active' : ''}`}
        >
          {t('nav.languages')}
        </NavLink>
      </nav>

      <Routes>
        <Route path="/info" element={
          <div className="items-container">
            <InfoPage />
          </div>
        } />
        <Route path="/languages" element={
          <div className="items-container">
            <LanguagesPage />
          </div>
        } />
        <Route path="/" element={
          <>

      {error && (
        <div className="error-message">
          <strong>{t('messages.error')}</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <strong>{t('messages.success')}</strong> {successMessage}
        </div>
      )}

      {loading ? (
        <div className="loading">{t('messages.loading')}</div>
      ) : (
        <div className="items-container">
          <div className="add-button-container">
            <div className="db-selector">
              <label htmlFor="db-select">{t('menu.selectDatabase')}:</label>
              <select
                id="db-select"
                value={dbTable}
                onChange={(e) => setDbTable(e.target.value)}
                className="db-select"
              >
                <option value="eu">{t('databases.eu')}</option>
                <option value="us">{t('databases.us')}</option>
              </select>
            </div>
            <button className="btn btn-primary btn-add" onClick={handleOpenCreateModal}>
              {t('buttons.add')}
            </button>
            <button className="btn btn-secondary btn-help" onClick={() => window.open(`${process.env.REACT_APP_API_BASE_URL}/help.php`, '_blank')}>
              {t('buttons.help')}
            </button>
          </div>

          <h2>{t('messages.itemCount')} ({filteredItems.length})</h2>
          <table className="items-list list-view">
            <thead>
              <tr>
                <th>{t('table.headers.id')}</th>
                <th className="sortable" onClick={() => handleSort('name_gr')}>
                  {t('table.headers.nameGr')} {sortConfig.key === 'name_gr' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                </th>
                <th className="sortable" onClick={() => handleSort('name_en')}>
                  {t('table.headers.nameEn')} {sortConfig.key === 'name_en' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                </th>
                <th className="sortable" onClick={() => handleSort('energy')}>
                  {t('table.headers.energy')} {sortConfig.key === 'energy' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                </th>
                <th className="sortable" onClick={() => handleSort('carbs')}>
                  {t('table.headers.carbs')} {sortConfig.key === 'carbs' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                </th>
                <th className="sortable" onClick={() => handleSort('fat')}>
                  {t('table.headers.fat')} {sortConfig.key === 'fat' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                </th>
                <th className="sortable" onClick={() => handleSort('proteins')}>
                  {t('table.headers.proteins')} {sortConfig.key === 'proteins' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                </th>
                <th className="sortable" onClick={() => handleSort('grams')}>
                  {t('table.headers.grams')} {sortConfig.key === 'grams' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                </th>
                <th className="sortable" onClick={() => handleSort('quantity')}>
                  {t('table.headers.quantity')} {sortConfig.key === 'quantity' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
                </th>
                <th>{t('table.headers.unit')}</th>
                <th>{t('table.headers.actions')}</th>
              </tr>
                <tr className="filter-row">
                  <th></th>
                  <th>
                    <input
                      type="text"
                      className="column-filter-input"
                      placeholder={t('table.filterPlaceholder')}
                      value={columnFilters.name_gr}
                      onChange={(e) => handleColumnFilterChange('name_gr', e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="column-filter-input"
                      placeholder={t('table.filterPlaceholder')}
                      value={columnFilters.name_en}
                      onChange={(e) => handleColumnFilterChange('name_en', e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="column-filter-input"
                      placeholder={t('table.filterPlaceholder')}
                      value={columnFilters.energy}
                      onChange={(e) => handleColumnFilterChange('energy', e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="column-filter-input"
                      placeholder={t('table.filterPlaceholder')}
                      value={columnFilters.carbs}
                      onChange={(e) => handleColumnFilterChange('carbs', e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="column-filter-input"
                      placeholder={t('table.filterPlaceholder')}
                      value={columnFilters.fat}
                      onChange={(e) => handleColumnFilterChange('fat', e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="column-filter-input"
                      placeholder={t('table.filterPlaceholder')}
                      value={columnFilters.proteins}
                      onChange={(e) => handleColumnFilterChange('proteins', e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="column-filter-input"
                      placeholder={t('table.filterPlaceholder')}
                      value={columnFilters.grams}
                      onChange={(e) => handleColumnFilterChange('grams', e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="column-filter-input"
                      placeholder={t('table.filterPlaceholder')}
                      value={columnFilters.quantity}
                      onChange={(e) => handleColumnFilterChange('quantity', e.target.value)}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      className="column-filter-input"
                      placeholder={t('table.filterPlaceholder')}
                      value={columnFilters.unit}
                      onChange={(e) => handleColumnFilterChange('unit', e.target.value)}
                    />
                  </th>
                  <th>
                    <button 
                      className="btn btn-secondary btn-small btn-reset-filters" 
                      onClick={handleResetSearch}
                      title={t('buttons.reset')}
                    >
                      🔄
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="empty-cell">
                      <div className="empty-state">
                        <div className="empty-state-icon">🍽️</div>
                        <h3>{t('messages.noItems')}</h3>
                        <p>{t('messages.noItemsDescription')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>#{item.id}</td>
                    <td>{item.name_gr}</td>
                    <td>{item.name_en}</td>
                    <td>{item.energy || 0}</td>
                    <td>{item.carbs || 0}</td>
                    <td>{item.fat || 0}</td>
                    <td>{item.proteins || 0}</td>
                    <td>{item.grams || 0}</td>
                    <td>{item.quantity || 0}</td>
                    <td>{item.unit || '-'}</td>
                    <td>
                      <button
                        className="btn btn-primary btn-small"
                        onClick={() => handleEdit(item)}
                        title={t('buttons.edit')}
                      >
                        ✏️
                      </button>
                      <button
                        className="btn btn-danger btn-small"
                        onClick={() => {
                          if (
                            window.confirm(
                              `${t('messages.deleteConfirm')} "${item.name_gr}" ${t('messages.deleteConfirmEnd')}`
                            )
                          ) {
                            handleDelete(item.id);
                          }
                        }}
                        title={t('buttons.delete')}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
        </div>
      )}

      {/* Modal for creating */}
      <Modal isOpen={isCreateModalOpen} onClose={handleCloseCreateModal}>
        <ItemForm
          item={null}
          onSubmit={handleCreate}
          onCancel={handleCloseCreateModal}
        />
      </Modal>

      {/* Modal for editing */}
      <Modal isOpen={editingItem !== null} onClose={handleCancelEdit}>
        <ItemForm
          item={editingItem}
          onSubmit={handleUpdate}
          onCancel={handleCancelEdit}
        />
      </Modal>

          </>
        } />
      </Routes>

      <footer className="footer">
        <p>&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
      </footer>
    </div>
  );
}

export default App;
